import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Quote } from '../../types/quote';
import { ReviewForm } from './ReviewForm';
import { QuoteDetailsInfo } from '../quotes/QuoteDetailsInfo';
import JobListCardHeader from '../jobs/JobListCardHeader';
import { useReviewStore } from '../../stores/reviewStore';
import { Button } from '../common/Button';
import { computeQuoteTotalAmount } from '../../utils/jobUtils';
import { Review } from '../../types/review';

interface RecruiterReviewProps {
  quote: Quote;
  onComplete?: () => void;
  onClose: () => void;
}

const RecruiterReview: React.FC<RecruiterReviewProps> = ({ quote, onComplete, onClose }) => {
  const { t } = useTranslation(['reviews', 'common']);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState<null | Review>(null);

  const { submitReview } = useReviewStore();
  const grandTotal = computeQuoteTotalAmount(quote);
  const freelancerName = `${quote.quote_owner.first_name} ${quote.quote_owner.last_name}`;

  const handleSend = async () => {
    if (!formData) return;
    try {
      setSending(true);
      await submitReview(formData);
      onComplete?.();
    } catch (err) {
      console.error('Erreur envoi review:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <h2 className="text-[#304659] text-2xl font-bold">
        {t('reviewModalTitle', { ns: 'reviews', defaultValue: 'Évaluation du projet' })}
      </h2>

      <div className="my-4">
        <JobListCardHeader job={quote.job} grandTotal={grandTotal} projectMode={true} />
      </div>

      <hr className="border-[#E1E9F0] my-4" />

      <h2 className="text-[#304659] text-xl font-bold mb-4">
        {t('bidder', { ns: 'reviews', defaultValue: 'Pigiste soumissionnaire' })}
      </h2>

      <QuoteDetailsInfo
        quote={quote}
        job={quote.job}
        variant="quote"
        headerOnly={true}
        isProject={true}
      />

      <div className="mt-6">
        <h3 className="text-lg font-bold text-[#304659] mb-2">
          {t('myReview', { ns: 'reviews', defaultValue: 'Mon évaluation' })}
        </h3>
        <ReviewForm
          quoteId={quote.id}
          reviewedId={quote.quote_owner.id}
          username={freelancerName}
          onChange={setFormData}
        />
      </div>

      <div className="flex justify-end space-x-2 mt-4">
        <Button
          type="button"
          onClick={onClose}
          className="w-full md:w-auto md:px-8"
          variant="simple-text"
        >
          {t('common:form_actions.cancel_close', { ns: 'common' })}
        </Button>

        <Button
          className="w-full md:w-auto md:px-8"
          variant="primary"
          type="button"
          onClick={handleSend}
          disabled={sending}
        >
          {sending
            ? t('common:form_actions.sending', { ns: 'common', defaultValue: 'Envoi...' })
            : t('common:form_actions.send', { ns: 'common', defaultValue: 'Envoyer' })}
        </Button>
      </div>
    </>
  );
};

export default RecruiterReview;
